import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { cacheHeaders } from '~/lib/site/cache'
import { DEFAULT_CHAPTER, type Chapter } from '~/lib/adb/client'
import { useChapterEvents } from '~/lib/adb/chapter'
import { loadRegions, loadSeed } from '~/lib/adb/events-loader'
import { parsePageId } from '~/lib/adb/page-id'
import { pageMeta } from '~/lib/site/seo'
import { Container } from '~/components/ui/Container'
import { EventList } from '~/components/events/EventList'
import { PageTitle } from '~/components/ui/PageTitle'
import { pageTop } from '~/components/ui/PageHero'

export const Route = createFileRoute('/events/$pageId/')({
  // Parse the PageID once, so the loader and component get a number (NaN if
  // the URL segment isn't one).
  params: {
    parse: ({ pageId }) => ({ pageId: parsePageId(pageId) }),
    stringify: ({ pageId }) => ({ pageId: String(pageId) }),
  },
  loader: async ({ params: { pageId }, context: { queryClient } }) => {
    if (Number.isNaN(pageId)) throw notFound()
    const [seed, regions] = await Promise.all([
      loadSeed(queryClient, pageId),
      loadRegions(queryClient),
    ])
    const chapter: Chapter | null =
      Object.values(regions ?? {})
        .flat()
        .find((c) => c.ID === pageId) ??
      (pageId === DEFAULT_CHAPTER.ID ? DEFAULT_CHAPTER : null)
    return { seed, chapter }
  },
  headers: () => cacheHeaders.events,
  head: ({ loaderData }) => {
    return pageMeta({
      title: `${loaderData?.chapter?.Name ?? 'Chapter'} Events`,
      description: `Upcoming animal rights events for ${loaderData?.chapter?.Name ?? 'this chapter'}.`,
    })
  },
  component: ChapterEvents,
})

function ChapterEvents() {
  const { seed, chapter: found } = Route.useLoaderData()
  const pageId = seed.pageId
  const chapter: Chapter = found ?? {
    Name: 'this chapter',
    ID: pageId,
    FbURL: '',
    Flag: '',
  }
  const { upcoming, failed } = useChapterEvents(pageId, seed)
  return (
    <div className={`${pageTop} pb-[100px]`}>
      <Container width="wide">
        <p className="mb-2">
          <Link
            to="/events/chapters"
            className="font-bold text-accent hover:underline"
          >
            ← All chapters
          </Link>
        </p>
        <PageTitle className="mb-10">{chapter.Name} events</PageTitle>
        <EventList events={upcoming} failed={failed} chapter={chapter} />
      </Container>
    </div>
  )
}
